import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Trash, Briefcase, EnvelopeSimple, Clock, Archive, ArrowUUpLeft } from '@phosphor-icons/react';
import { API } from './adminConstants';

export function JobArchiveView() {
  const { getAuthHeader } = useAuth();
  const [subTab, setSubTab] = useState('applications');
  const [archivedApps, setArchivedApps] = useState([]);
  const [archivedJobs, setArchivedJobs] = useState([]);

  const loadApps = () => axios.get(`${API}/admin/archive/applications`, { headers: getAuthHeader() }).then(r => setArchivedApps(r.data)).catch(() => {});
  const loadJobs = () => axios.get(`${API}/admin/archive/jobs`, { headers: getAuthHeader() }).then(r => setArchivedJobs(r.data)).catch(() => {});
  useEffect(() => { loadApps(); loadJobs(); }, []);

  const restoreJob = async (id) => {
    try { await axios.post(`${API}/admin/archive/jobs/${id}/restore`, {}, { headers: getAuthHeader() }); toast.success('Job restored'); loadJobs(); } catch { toast.error('Failed'); }
  };
  const deleteArchivedJob = async (id) => {
    try { await axios.delete(`${API}/admin/archive/jobs/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); loadJobs(); } catch { toast.error('Failed'); }
  };
  const deleteArchivedApp = async (id) => {
    try { await axios.delete(`${API}/admin/archive/applications/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); loadApps(); } catch { toast.error('Failed'); }
  };

  const getAutoDeleteDate = (archivedAt) => {
    const d = new Date(archivedAt);
    d.setMonth(d.getMonth() + 6);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div data-testid="job-archive-view">
      <h1 className="font-outfit font-bold text-2xl text-slate-900">Job Archive</h1>
      <p className="text-slate-500 text-sm mt-1">Archived jobs and applications</p>

      <div className="flex gap-2 mt-4 mb-6">
        <Button variant={subTab === 'applications' ? 'default' : 'outline'} size="sm" onClick={() => setSubTab('applications')} className={subTab === 'applications' ? 'bg-lime-600 hover:bg-lime-700' : ''} data-testid="archive-tab-applications">
          <EnvelopeSimple size={14} className="mr-1" />Applications ({archivedApps.length})
        </Button>
        <Button variant={subTab === 'jobs' ? 'default' : 'outline'} size="sm" onClick={() => setSubTab('jobs')} className={subTab === 'jobs' ? 'bg-lime-600 hover:bg-lime-700' : ''} data-testid="archive-tab-jobs">
          <Briefcase size={14} className="mr-1" />Job Listings ({archivedJobs.length})
        </Button>
      </div>

      {subTab === 'applications' && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-100 bg-amber-50/50">
            <p className="text-xs text-amber-700"><Clock size={12} className="inline mr-1" />Archived applications auto-delete after 6 months</p>
          </div>
          <div className="divide-y divide-slate-100">
            {archivedApps.map(a => (
              <div key={a.id} className="p-4 flex items-center justify-between" data-testid={`archived-app-${a.id}`}>
                <div>
                  <p className="font-medium text-slate-900">{a.first_name} {a.last_name}</p>
                  <p className="text-sm text-slate-500">{a.email}</p>
                  <p className="text-xs text-lime-600">Applied for: {a.job_title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${a.status === 'hired' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{a.status}</span>
                    <span className="text-[10px] text-slate-400">Archived: {new Date(a.archived_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <span className="text-[10px] text-amber-600">Auto-deletes: {getAutoDeleteDate(a.archived_at)}</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => deleteArchivedApp(a.id)} className="text-red-500 border-red-200 hover:bg-red-50"><Trash size={14} /></Button>
              </div>
            ))}
            {archivedApps.length === 0 && <div className="p-8 text-center text-slate-500">No archived applications</div>}
          </div>
        </div>
      )}

      {subTab === 'jobs' && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-500"><Archive size={12} className="inline mr-1" />Archived job listings are kept for future reference</p>
          </div>
          <div className="divide-y divide-slate-100">
            {archivedJobs.map(j => (
              <div key={j.id} className="p-4 flex items-center justify-between" data-testid={`archived-job-${j.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">{j.title}</p>
                    <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{j.ref}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{j.department} | {j.location} | {j.salary}</p>
                  <p className="text-[10px] text-slate-400 mt-1">Archived: {new Date(j.archived_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} by {j.archived_by}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => restoreJob(j.id)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid={`restore-job-${j.id}`}><ArrowUUpLeft size={14} className="mr-1" />Restore</Button>
                  <Button variant="outline" size="sm" onClick={() => deleteArchivedJob(j.id)} className="text-red-500 border-red-200 hover:bg-red-50"><Trash size={14} /></Button>
                </div>
              </div>
            ))}
            {archivedJobs.length === 0 && <div className="p-8 text-center text-slate-500">No archived job listings</div>}
          </div>
        </div>
      )}
    </div>
  );
}
